// A trailing `Z` or an explicit `±HH:MM` / `±HHMM` offset — the only cases
// where the instant is unambiguous and safe to re-express in Eastern time.
// Without one, `new Date(raw)` would anchor the value to the analyst's local
// zone (see `formatChangeDate`).
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

// Fixed to America/New_York so every analyst sees the same wall-clock time
// regardless of their machine's locale — caps2 rendered these rows in EST via
// `DateUtil.dateTimeConverter`, and the Workbench mirrors that for parity.
const EST_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

/**
 * Formats a recap change-history timestamp as `MM/DD/YYYY hh:mm:ss AM/PM` in
 * Eastern time (caps2 parity). recap's exact wire format is not pinned by the
 * BFF contract (the DTO types `date` as an opaque string), so an unparseable or
 * empty value falls back to the raw string verbatim rather than rendering
 * "Invalid Date" — the table shows values exactly as received when in doubt.
 *
 * Assembled from `formatToParts` rather than `format()` so the date and time are
 * joined by a single space (caps2's `DateUtil.dateTimeConverter` output), not
 * the `", "` that `Intl` inserts between the two.
 *
 * Only strings that carry an explicit zone (`Z` or a numeric offset) are
 * re-expressed in Eastern time; a zone-less or date-only value is rendered
 * verbatim rather than being anchored to the viewer's local zone and shifted
 * (which would defeat the whole point of the ET normalization). This is the
 * conservative reading of an unpinned recap wire format — show it as received
 * when the instant is ambiguous.
 * @param {string} raw - The raw timestamp string from the BFF (may be empty).
 * @returns {string} The EST-formatted timestamp, or the raw string if ambiguous/unparseable.
 */
export function formatChangeDate(raw: string): string {
  if (!raw) {
    return "";
  }
  if (!HAS_TIMEZONE.test(raw)) {
    return raw;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const parts = new Map(EST_FORMAT.formatToParts(parsed).map((part) => [part.type, part.value]));
  return `${parts.get("month")}/${parts.get("day")}/${parts.get("year")} ${parts.get("hour")}:${parts.get("minute")}:${parts.get("second")} ${parts.get("dayPeriod")}`;
}
